import React, { Suspense, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { selectCurrentRoleId } from "../../store/slices/CreateRoleSlice";
import {
  createDataRestriction,
  deleteDataRestriction,
  getRoleSetupDataRestrictionDropdownData,
  getDataForTable,
} from "../../services/RequestAccess/RequestAccess";
import { triggerToast } from "../../utils/toast";
import { LoadingSpinner } from "cprime-foundational-components";
import RoleSetupPanleComponent from "../CreateEditRoleCommonFolder/RoleSetupPanelComponent";
import CreateEditRoleDataDomainPanelBody from "./CreateEditRoleDataDomainPanelBody";
import { useParams } from "react-router-dom";
import GridTableCommon from "../GridTableCommon/GridTableCommon";
import { ColumnFilter, SortingState } from "@tanstack/react-table";
import { formatFilterValue } from "../../utils/formatFilters";
import * as style from "../CreateEditRoleAPI/CreateEditRoleSidePanel.module.scss";
import { useIdToken } from "../../hooks/useAuthSession";

interface CreateEditRoleDataDomainProps {
  refresh?: number;
  isEditable?: boolean;
}

const CreateEditRoleDataDomain: React.FC<CreateEditRoleDataDomainProps> = ({
  refresh = 0,
  isEditable,
}) => {
  const scssClass = style as any;
  const idToken = useIdToken();
  const currentRoleId = useSelector(selectCurrentRoleId);
  const [refreshValue, setRefreshValue] = useState<number>(0);
  const [openEditPanel, setOpenEditPanel] = useState(false);
  const { id } = useParams();
  const [domainOptionList, setDomainOptionList] = useState<Array<any>>([]);
  const [selectedDomainOptions, setDelectedDomainOptions] = useState<{
    id: string | number;
    label: string;
    allowed: Array<any>;
    restriction: Array<any>;
  }>({ id: "", label: "", allowed: [], restriction: [] });
  const [editModeData, setEditModeData] = useState(null);
  const [selectedRows, setSelectedRows] = useState<any[]>([]);
  const [filters, setFilters] = useState<ColumnFilter[]>([]);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [pagination, setPagination] = useState({ page: 0, rowsPerPage: 25 });
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [gridData, setGridData] = useState<any>({
    rows: [],
    totalCount: 0,
  });

  const handleClose = () => {
    setOpenEditPanel(false);
  };

  const handleDeleteDataDomain = async (
    rows: any,
    currentRoleId: number,
    isAll?: boolean,
  ) => {
    try {
      const domainIds = rows?.map((row: any) => row);
      const restrictions = domainIds.map((domainId: number) => ({
        roleId: currentRoleId,
        domainId: domainId,
      }));

      const response = await deleteDataRestriction(restrictions);
      if (response.error) {
        triggerToast(`${response.error}`, "error");
      } else if (response.status === "SUCCESS") {
        setRefreshValue((prev) => prev + 1);
        triggerToast(`${response.message}`, "success");
        if (isAll) setSelectedRows([]);
      }
    } catch (error: any) {}
  };

  const getDataRestrictionDropdownData = async () => {
    try {
      const res = await getRoleSetupDataRestrictionDropdownData(
        id?.split("-")[1],
      );
      setDomainOptionList(res?.data);
    } catch (err) {
      // console.error("error fetching api details with appId", err);
    }
  };
  useEffect(() => {
    getDataRestrictionDropdownData();
  }, []);

  const handleRoleEditomain = (row: any) => {
    setOpenEditPanel(true);
    const rowdata = row?.row?.original;
    setEditModeData(rowdata);
  };

  const fetchData = async () => {
    setIsLoading(true);
    const resp = await getDataForTable(
      `/datasetup/domaindetails?role_id=${currentRoleId}`,
      {
        sortingFilter: {
          filter:
            filters?.length > 0
              ? (filters || [])?.map((item: any) =>
                  formatFilterValue(item?.value, item?.id),
                )
              : [],
          sort: sorting?.map((item: any) => {
            return { columnName: item?.id, type: item?.desc ? "DESC" : "ASC" };
          }),
          page: {
            limit: pagination?.rowsPerPage,
            offset: pagination?.page,
          },
        },
      },
    );
    resp.data.rows = resp?.data?.rows?.map((row: any) => {
      return {
        ...row,
        action: row?.action?.map((action: any) => {
          return {
            ...action,
            disabled: isEditable
              ? action?.disabled
              : action?.disabled ||
                action?.value === "Edit" ||
                action?.value === "Delete",
          };
        }),
      };
    });
    setGridData(resp?.data);
    setIsLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, [sorting, pagination, refreshValue, refresh]);
  useEffect(() => {
    setPagination({
      page: 0,
      rowsPerPage: pagination?.rowsPerPage,
    });
  }, [filters]);

  return (
    <>
      <div className={scssClass.tableContainer}>
        {idToken && (
          <GridTableCommon
            gridData={gridData ?? {}}
            selectedRows={selectedRows}
            refresh={refreshValue + refresh}
            onSelectionChange={setSelectedRows}
            style={{
              height: "calc(100vh - 38rem)",
              maxWidth: "calc(100vw - 4rem)",
            }}
            keyId="domain_id"
            title="Data Restriction"
            loading={isLoading}
            filters={filters}
            sorting={sorting}
            setSorting={setSorting}
            setFilters={setFilters}
            pagination={pagination}
            setPagination={setPagination}
            cellActions={[
              {
                event: "Edit",
                callback: (_event: any, row: any) => {
                  handleRoleEditomain(row);
                },
                icon: true,
              },
              {
                event: "Delete",
                callback: (_event: any, row: any) => {
                  handleDeleteDataDomain(
                    [row?.row?.original?.domain_id],
                    currentRoleId,
                  );
                },
                icon: true,
              },
            ]}
            rowActions={[
              {
                event: "Delete",
                callback: (_event: any, rows: any) => {
                  handleDeleteDataDomain(selectedRows, currentRoleId, true);
                },
              },
            ]}
          />
        )}
      </div>
      {openEditPanel && (
        <Suspense fallback={<LoadingSpinner fullScreen={true} />}>
          <RoleSetupPanleComponent
            open={openEditPanel}
            title="Edit Domain Restrictions"
            isEdit={false}
            onClose={handleClose}
            BodyComponent={
              <Suspense fallback={<LoadingSpinner fullScreen={true} />}>
                <CreateEditRoleDataDomainPanelBody
                  domainOptionList={domainOptionList}
                  handleChange={(data) => setDelectedDomainOptions(data)}
                  editModeData={editModeData}
                />
              </Suspense>
            }
            tabValue={3}
            submitLabel={"Save Domain"}
            onSubmit={async () => {
              const response = await createDataRestriction({
                roleId: currentRoleId,
                domainId: selectedDomainOptions.id,
                allowedSubDomainName:
                  selectedDomainOptions?.allowed?.length > 0
                    ? selectedDomainOptions?.allowed?.map((d) => d?.id)
                    : [],
                deniedSubDomainName:
                  selectedDomainOptions?.restriction?.length > 0
                    ? selectedDomainOptions?.restriction?.map((d) => d?.id)
                    : [],
              });
              if (response) {
                setRefreshValue((prev) => prev + 1);
              }
              triggerToast(
                `${response?.message}`,
                response?.status?.toLowerCase(),
              );
              handleClose();
            }}
          />
        </Suspense>
      )}
    </>
  );
};

export default CreateEditRoleDataDomain;
